import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FolderOpen, Plus, LogOut, Trash2, Download, ArrowLeft, Search, User, Menu, X, Calendar, Users, AlertTriangle } from 'lucide-react';
import { BUSINESS_TYPES } from '../constants/businessTypes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = ({ user, onLogout }) => {
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [userRole, setUserRole] = useState('owner');
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('in_progress');
  const [showPendingReview, setShowPendingReview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [applicationTemplates, setApplicationTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [newProject, setNewProject] = useState({
    project_title: '',
    client_name: '',
    business_type: '',
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // CRM link dialog state
  const [crmDialogOpen, setCrmDialogOpen] = useState(false);
  const [crmAfmSearch, setCrmAfmSearch] = useState('');
  const [crmSearchResult, setCrmSearchResult] = useState(null);
  const [crmSearchLoading, setCrmSearchLoading] = useState(false);
  const [linkingProjectId, setLinkingProjectId] = useState(null);
  
  const navigate = useNavigate();

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/user/${user.user_id}/backup`, {
        responseType: 'blob',
        timeout: 300000, // 5 minutes timeout for large backups
      });

      const totalFiles = response.headers['x-total-files'] || 'unknown';
      const totalSize = response.headers['x-total-size'] || 'unknown';
      const sizeMB = totalSize !== 'unknown' ? (totalSize / (1024 * 1024)).toFixed(2) : 'unknown';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute('download', `my_backup_${timestamp}.zip`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Backup ολοκληρώθηκε! ${totalFiles} αρχεία (${sizeMB} MB)`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Άγνωστο σφάλμα';
      toast.error(`Σφάλμα δημιουργίας backup: ${errorMsg}`);
      console.error('Backup error:', error);
    } finally {
      setBackupLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchTeamMembers();
    fetchUserRole();
    fetchApplicationTemplates();
    
    // Auto-refresh every 5 seconds to show updates from public submissions
    const interval = setInterval(() => {
      fetchProjects();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [activeFilter]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?user_id=${user.user_id}&status=${activeFilter}`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης έργων');
    }
  };

  const fetchApplicationTemplates = async () => {
    try {
      const response = await axios.get(`${API}/application-templates?user_id=${user.user_id}`);
      setApplicationTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members?user_id=${user.user_id}`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API}/user/info?user_id=${user.user_id}`);
      setUserRole(response.data.role || 'owner');
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const handleAssignProject = async (projectId, memberIds) => {
    try {
      // memberIds is now an array, join with comma
      const assignedTo = Array.isArray(memberIds) ? memberIds.join(',') : (memberIds || '');
      await axios.put(`${API}/projects/${projectId}/assign?user_id=${user.user_id}&assigned_to=${assignedTo}`);
      toast.success('Το έργο ανατέθηκε');
      fetchProjects();
    } catch (error) {
      toast.error('Σφάλμα ανάθεσης');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build URL with template_id if selected
      let url = `${API}/projects?user_id=${user.user_id}`;
      if (selectedTemplateId) {
        url += `&template_id=${selectedTemplateId}`;
      }
      
      // Include crm_customer_id if selected from CRM
      const projectData = { ...newProject };
      if (!projectData.business_type) delete projectData.business_type;
      
      await axios.post(url, projectData);
      toast.success('Το έργο δημιουργήθηκε' + (selectedTemplateId ? ' με το πρότυπο' : ''));
      setNewProject({
        project_title: '',
        client_name: '',
        business_type: '',
      });
      setSelectedTemplateId('');
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Φτάσατε το μέγιστο όριο έργων. Παρακαλώ αναβαθμίστε τη συνδρομή σας.');
      } else {
        toast.error('Σφάλμα δημιουργίας έργου');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (e, projectId, currentStatus) => {
    e.stopPropagation(); // Prevent navigation
    try {
      await axios.put(`${API}/projects/${projectId}/toggle-status`);
      const newStatus = currentStatus === 'in_progress' ? 'completed' : 'in_progress';
      toast.success(newStatus === 'completed' ? 'Το έργο ολοκληρώθηκε' : 'Το έργο επανήλθε σε εξέλιξη');
      fetchProjects();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης κατάστασης');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}?user_id=${user.user_id}`);
      toast.success('Το έργο διαγράφηκε');
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα διαγραφής έργου');
    }
  };

  // CRM Link functions
  const openCrmDialog = (projectId) => {
    setLinkingProjectId(projectId);
    setCrmAfmSearch('');
    setCrmSearchResult(null);
    setCrmDialogOpen(true);
  };

  const handleCrmSearch = async () => {
    if (!crmAfmSearch || crmAfmSearch.length !== 9) {
      toast.error('Εισάγετε 9 ψηφία ΑΦΜ');
      return;
    }
    
    setCrmSearchLoading(true);
    try {
      const response = await axios.get(`${API}/crm/customers/search-by-afm?afm=${crmAfmSearch}&user_id=${user.user_id}`);
      setCrmSearchResult(response.data);
    } catch (error) {
      setCrmSearchResult(null);
      toast.error('Δεν βρέθηκε πελάτης με αυτό το ΑΦΜ');
    } finally {
      setCrmSearchLoading(false);
    }
  };

  const handleLinkCrmCustomer = async () => {
    if (!linkingProjectId || !crmSearchResult) return;
    
    // If linking to new project form, just fill the client name
    if (linkingProjectId === 'new') {
      setNewProject(prev => ({ 
        ...prev, 
        client_name: crmSearchResult.company_name,
        crm_customer_id: crmSearchResult.customer_id
      }));
      toast.success(`Επιλέχθηκε: ${crmSearchResult.company_name}`);
      setCrmDialogOpen(false);
      return;
    }
    
    // Otherwise link existing project to CRM
    try {
      await axios.put(`${API}/projects/${linkingProjectId}/link-crm?user_id=${user.user_id}&crm_customer_id=${crmSearchResult.customer_id}`);
      toast.success(`Συνδέθηκε με: ${crmSearchResult.company_name}`);
      setCrmDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error('Σφάλμα σύνδεσης με CRM');
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter((project) => {
    // Filter by pending review (documents uploaded, need checking)
    if (showPendingReview && !(project.uploaded_for_review > 0)) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.project_title?.toLowerCase().includes(query) ||
      project.client_name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-md border border-slate-200"
        data-testid="mobile-menu-button"
      >
        <Menu className="h-6 w-6 text-slate-700" />
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay md:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Mobile Close Button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900" data-testid="user-dashboard-title">ESPA Manager</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => navigate('/select-module')}
              data-testid="back-to-modules-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Πίσω στην Επιλογή
            </Button>
            <div className="pt-4 border-t border-slate-200 mt-4">
              <button
                onClick={() => setActiveFilter('in_progress')}
                className={`w-full px-4 py-2 rounded-md text-left font-medium transition-colors ${
                  activeFilter === 'in_progress'
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="filter-in-progress"
              >
                <FolderOpen className="inline-block mr-2 h-4 w-4" />
                Έργα σε Εξέλιξη
              </button>
              <button
                onClick={() => setActiveFilter('completed')}
                className={`w-full px-4 py-2 rounded-md text-left font-medium transition-colors ${
                  activeFilter === 'completed'
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="filter-completed"
              >
                <FolderOpen className="inline-block mr-2 h-4 w-4" />
                Ολοκληρωμένα Έργα
              </button>
              
              {activeFilter === 'in_progress' && (
                <button
                  onClick={() => setShowPendingReview(prev => !prev)}
                  className={`w-full px-4 py-2 rounded-md text-left font-medium transition-colors ${
                    showPendingReview
                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="filter-pending-review"
                >
                  <AlertTriangle className="inline-block mr-2 h-4 w-4" />
                  Με Εκκρεμότητες
                </button>
              )}
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      <div className="main-content">
        <div className="p-4 md:p-8 pt-16 md:pt-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="projects-heading">
                Τα Έργα Μου
              </h1>
              <p className="text-slate-600 mt-1 text-sm md:text-base">Διαχείριση έργων ESPA</p>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              {/* Search Bar */}
              <div className="relative flex-1 md:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Αναζήτηση έργου..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-64"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={handleBackup}
                disabled={backupLoading}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                data-testid="backup-button"
              >
                <Download className={`mr-2 h-4 w-4 ${backupLoading ? 'animate-bounce' : ''}`} />
                {backupLoading ? 'Δημιουργία...' : 'Backup Αρχείων'}
              </Button>

              {/* Confirmation Dialog - Only show for owners */}
              {userRole === 'owner' && (
              <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-project-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Προσθήκη Έργου
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Δημιουργία Νέου Έργου Αίτησης</AlertDialogTitle>
                    <AlertDialogDescription>
                      Είστε σίγουροι ότι θέλετε να δημιουργήσετε νέο έργο αίτησης; 
                      Αυτό θα μειώσει το διαθέσιμο όριο έργων σας.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { setConfirmDialogOpen(false); setDialogOpen(true); }}>
                      Ναι, συνέχεια
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}

              {/* Create Project Dialog */}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent data-testid="add-project-dialog" aria-describedby="add-project-description">
                <DialogHeader>
                  <DialogTitle>Νέο Έργο ESPA</DialogTitle>
                </DialogHeader>
                <p id="add-project-description" className="text-sm text-slate-600 mb-4">
                  Δημιουργήστε νέο έργο ESPA με τίτλο και όνομα πελάτη
                </p>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-title">Τίτλος Προγράμματος</Label>
                    <Input
                      id="project-title"
                      value={newProject.project_title}
                      onChange={(e) => setNewProject({ ...newProject, project_title: e.target.value })}
                      placeholder="π.χ. Αίτηση Digital Tools"
                      required
                      data-testid="project-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="client-name">Όνομα Πελάτη</Label>
                    <div className="flex gap-2">
                      <Input
                        id="client-name"
                        value={newProject.client_name}
                        onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                        placeholder="π.χ. KOUTROULIS ΑΕ"
                        required
                        data-testid="client-name-input"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setCrmAfmSearch('');
                          setCrmSearchResult(null);
                          setLinkingProjectId('new');
                          setCrmDialogOpen(true);
                        }}
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        data-testid="search-crm-btn"
                      >
                        <Users className="h-4 w-4 mr-1" />
                        CRM
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Ή αναζητήστε πελάτη από το CRM με ΑΦΜ
                    </p>
                  </div>

                  {/* Template Selection */}
                  {applicationTemplates.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="template-select">Πρότυπο Αίτησης (προαιρετικό)</Label>
                      <Select
                        value={selectedTemplateId || "none"}
                        onValueChange={(val) => setSelectedTemplateId(val === "none" ? "" : val)}
                      >
                        <SelectTrigger data-testid="template-select">
                          <SelectValue placeholder="Επιλέξτε πρότυπο..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Χωρίς πρότυπο</SelectItem>
                          {applicationTemplates.map(template => (
                            <SelectItem key={template.template_id} value={template.template_id}>
                              {template.name} ({template.document_count || template.documents?.length || 0} δικαιολογητικά)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Τα δικαιολογητικά του προτύπου θα προστεθούν αυτόματα
                      </p>
                    </div>
                  )}

                  {/* Business Type Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="business-type">Μορφή Επιχείρησης (προαιρετικό)</Label>
                    <Select
                      value={newProject.business_type || "none"}
                      onValueChange={(val) => setNewProject({ ...newProject, business_type: val === "none" ? "" : val })}
                    >
                      <SelectTrigger data-testid="business-type-select">
                        <SelectValue placeholder="Επιλέξτε μορφή..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Χωρίς επιλογή</SelectItem>
                        {BUSINESS_TYPES.map(bt => (
                          <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Φιλτράρει τα δικαιολογητικά του προτύπου βάσει μορφής επιχείρησης
                    </p>
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="create-project-submit-button">
                    {loading ? 'Δημιουργία...' : 'Δημιουργία Έργου'}
                  </Button>
                </form>
              </DialogContent>
              </Dialog>
            </div>
          </div>

          {filteredProjects.length === 0 ? (
            <div className="text-center py-16" data-testid="no-projects-message">
              <FolderOpen className="mx-auto h-16 w-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {searchQuery ? 'Δεν βρέθηκαν έργα' : 'Δεν υπάρχουν έργα'}
              </h3>
              <p className="text-slate-600">
                {searchQuery 
                  ? 'Δοκιμάστε διαφορετική αναζήτηση' 
                  : userRole === 'owner' 
                    ? 'Πατήστε "Προσθήκη Έργου" για να ξεκινήσετε'
                    : 'Δεν έχουν ανατεθεί έργα ακόμα'
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {filteredProjects.map((project) => (
                <div
                  key={project.project_id}
                  className="bg-white border border-slate-200 rounded-md p-6 hover:shadow-md transition-shadow"
                  data-testid={`project-card-${project.project_id}`}
                >
                  <div onClick={() => navigate(`/projects/${project.project_id}`)} className="cursor-pointer">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-slate-900 mb-1">{project.project_title}</h3>
                        <p className="text-sm text-slate-600">{project.client_name}</p>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        {project.client_submitted_at && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs font-medium animate-pulse">
                            ✓ Νέα Υποβολή
                          </span>
                        )}
                        {project.pending_documents > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                            {project.pending_documents} εκκρεμή
                          </span>
                        )}
                        {project.uploaded_for_review > 0 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                            {project.uploaded_for_review} για έλεγχο
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-slate-600">
                      <div className="flex justify-between">
                        <span>Έλεγχοι:</span>
                        <span className="font-medium text-slate-900">{project.checks_used}/{project.max_checks}</span>
                      </div>
                      {project.deadline && (
                        <div className="flex justify-between">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-orange-500" />
                            Προθεσμία:
                          </span>
                          <span className="font-medium text-orange-600">
                            {new Date(project.deadline).toLocaleDateString('el-GR')}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Έναρξη:</span>
                        <span>{new Date(project.created_at).toLocaleDateString('el-GR')}</span>
                      </div>
                      {project.assigned_to_names && project.assigned_to_names.length > 0 && (
                        <div className="flex justify-between">
                          <span>Υπεύθυνοι:</span>
                          <span className="font-medium text-slate-900 flex items-center gap-1 flex-wrap justify-end">
                            <User className="h-3 w-3" />
                            {project.assigned_to_names.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                    {/* Multi-select assignment - only for owners */}
                    {userRole === 'owner' && teamMembers.length > 0 && (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <p className="text-xs font-medium text-slate-600">Ανάθεση σε υπαλλήλους:</p>
                        <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2 bg-white space-y-1">
                          {teamMembers.map((member) => {
                            const currentAssigned = Array.isArray(project.assigned_to) ? project.assigned_to : [];
                            const isChecked = currentAssigned.includes(member.user_id);
                            return (
                              <label 
                                key={member.user_id} 
                                className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    let newAssigned;
                                    if (e.target.checked) {
                                      newAssigned = [...currentAssigned, member.user_id];
                                    } else {
                                      newAssigned = currentAssigned.filter(id => id !== member.user_id);
                                    }
                                    handleAssignProject(project.project_id, newAssigned);
                                  }}
                                  className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-sm text-slate-700">{member.full_name || member.email}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* CRM Link button - Only show for owners */}
                    {userRole === 'owner' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openCrmDialog(project.project_id); }}
                        className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                          project.crm_customer_id 
                            ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' 
                            : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`link-crm-${project.project_id}`}
                      >
                        <Users className="h-4 w-4" />
                        {project.crm_customer_id ? 'Συνδεδεμένος CRM' : 'Σύνδεση CRM'}
                      </button>
                    )}

                    <button
                      onClick={(e) => handleToggleStatus(e, project.project_id, project.status)}
                      className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        project.status === 'completed'
                          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                      data-testid={`toggle-status-${project.project_id}`}
                    >
                      {project.status === 'completed' ? '✓ Ολοκληρωμένο Έργο' : '⟳ Έργο σε Εξέλιξη'}
                    </button>

                    {/* Delete button - Only show for owners */}
                    {userRole === 'owner' && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-3 py-2 rounded-md text-sm font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors flex items-center justify-center gap-2"
                          data-testid={`delete-project-${project.project_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          Διαγραφή Έργου
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Διαγραφή Έργου</AlertDialogTitle>
                          <AlertDialogDescription>
                            Είστε σίγουροι ότι θέλετε να διαγράψετε το έργο "{project.project_title}";
                            Όλα τα δικαιολογητικά και αρχεία θα διαγραφούν οριστικά.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteProject(project.project_id)}
                            className="bg-rose-600 hover:bg-rose-700"
                          >
                            Διαγραφή
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CRM Link Dialog */}
      <Dialog open={crmDialogOpen} onOpenChange={setCrmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Σύνδεση με Πελάτη CRM</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Αναζήτηση με ΑΦΜ (9 ψηφία)</Label>
              <div className="flex gap-2">
                <Input
                  value={crmAfmSearch}
                  onChange={(e) => setCrmAfmSearch(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  placeholder="123456789"
                  maxLength={9}
                  data-testid="crm-afm-search"
                />
                <Button 
                  onClick={handleCrmSearch} 
                  disabled={crmSearchLoading || crmAfmSearch.length !== 9}
                >
                  {crmSearchLoading ? '...' : 'Αναζήτηση'}
                </Button>
              </div>
            </div>

            {crmSearchResult && (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-medium text-slate-900">{crmSearchResult.company_name}</h4>
                <p className="text-sm text-slate-600">ΑΦΜ: {crmSearchResult.tax_id}</p>
                {crmSearchResult.contact_person && (
                  <p className="text-sm text-slate-600">Υπεύθυνος: {crmSearchResult.contact_person}</p>
                )}
                {crmSearchResult.phone && (
                  <p className="text-sm text-slate-600">Τηλ: {crmSearchResult.phone}</p>
                )}
                <Button 
                  className="mt-3 w-full" 
                  onClick={handleLinkCrmCustomer}
                  data-testid="confirm-crm-link"
                >
                  Σύνδεση με αυτόν τον πελάτη
                </Button>
              </div>
            )}

            <p className="text-xs text-slate-500">
              Αναζητήστε πελάτη από το CRM με ΑΦΜ. Το όνομα πελάτη θα ενημερωθεί αυτόματα.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default UserDashboard;